import { useException } from '@hooks/utils/useException';
import { useTemplate } from '@hooks/utils/useTemplate';
import { InterviewRepository } from '@repositories/interview.repository';
import type {
  MeetingRoomAttendeeDTO,
  MeetingRoomDTO,
} from 'server/model/meeting.room.dto';

const interviewRepository = new InterviewRepository(
  import.meta.env.VITE_API_URL,
);

export function useInterviewAPI() {
  const { withLogger } = useException();
  const { withStatusTemplate } = useTemplate();

  const fetchJoinMangerInterview = async (props: MeetingRoomDTO) => {
    try {
      const data = await interviewRepository.joinManagerInterview(props);

      return withStatusTemplate<MeetingRoomAttendeeDTO>({ status: true, data });
    } catch (error) {
      withLogger(error);

      return withStatusTemplate<MeetingRoomAttendeeDTO>({
        status: false,
        data: {
          JoinInfo: {
            Title: '',
            Name: '',
            Identity: '',
            Meeting: {},
            Attendee: {},
          },
        },
      });
    }
  };

  const fetchJoinInterview = async (props: MeetingRoomDTO) => {
    try {
      const data = await interviewRepository.joinInterview(props);

      return withStatusTemplate<MeetingRoomAttendeeDTO>({ status: true, data });
    } catch (error) {
      withLogger(error);

      return withStatusTemplate<MeetingRoomAttendeeDTO>({
        status: false,
        data: {
          JoinInfo: {
            Title: '',
            Name: '',
            Identity: '',
            Meeting: {},
            Attendee: {},
          },
        },
      });
    }
  };

  const leaveAttendeeInterview = async ({
    certificationCode,
    valuerSn,
  }: Pick<MeetingRoomDTO, 'certificationCode' | 'valuerSn'>) => {
    try {
      const data = await interviewRepository.leaveAttendee({
        certificationCode,
        valuerSn,
      });

      return withStatusTemplate<{ message: string }>({ status: true, data });
    } catch (error) {
      withLogger(error);

      return withStatusTemplate<{ message: string }>({
        status: false,
        data: { message: '' },
      });
    }
  };

  const leaveManagerInterview = async ({
    certificationCode,
    valuerSn,
  }: Pick<MeetingRoomDTO, 'certificationCode' | 'valuerSn'>) => {
    try {
      const data = await interviewRepository.leaveManager({
        certificationCode,
        valuerSn,
      });

      return withStatusTemplate<{ message: string }>({ status: true, data });
    } catch (error) {
      withLogger(error);

      return withStatusTemplate<{ message: string }>({
        status: false,
        data: { message: '' },
      });
    }
  };

  const fetchCreateChannelIfAbsent = async ({
    channelId,
  }: {
    channelId: string;
  }) => {
    try {
      const data = await interviewRepository.postCreateChannelIfAbsent({
        channelId,
      });
      return withStatusTemplate<{ invitationCode: string }>({
        status: true,
        data,
      });
    } catch (error) {
      withLogger(error);
      return withStatusTemplate<{ message: string }>({
        status: false,
        data: { message: '' },
      });
    }
  };

  const fetchGoogleMeetLink = async ({ channelId }: { channelId: string }) => {
    try {
      const data = await interviewRepository.postGoogleMeetLink({ channelId });
      return withStatusTemplate<{ googleMeet: string }>({ status: true, data });
    } catch (error) {
      withLogger(error);
      return withStatusTemplate<{ message: string }>({
        status: false,
        data: { message: '' },
      });
    }
  };

  const fetchAttendeesDetail = async ({
    certificationCode,
  }: {
    certificationCode: string;
  }) => {
    try {
      const data = await interviewRepository.getAttendeesDetail({
        certificationCode,
      });
      return withStatusTemplate<{
        attendees: { type: string; name: string; key: string }[];
      }>({ status: true, data });
    } catch (error) {
      withLogger(error);
      return withStatusTemplate<{ message: string }>({
        status: false,
        data: { message: '' },
      });
    }
  };

  return {
    fetchJoinMangerInterview,
    fetchJoinInterview,
    leaveAttendeeInterview,
    leaveManagerInterview,
    fetchCreateChannelIfAbsent,
    fetchGoogleMeetLink,
    fetchAttendeesDetail,
  };
}
